import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Package, Box, Scale, AlertTriangle } from 'lucide-react';
import { DriverDetails } from '@/types/driver.types';
import { cn } from '@/lib/utils';

interface DriverCapacityStatsProps {
  driver: DriverDetails;
}

function getProgressColor(percentage: number): string {
  if (percentage >= 85) return 'bg-destructive';
  if (percentage >= 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

function getProgressBgColor(percentage: number): string {
  if (percentage >= 85) return 'bg-destructive/20';
  if (percentage >= 60) return 'bg-yellow-500/20';
  return 'bg-green-500/20';
}

export function DriverCapacityStats({ driver }: DriverCapacityStatsProps) {
  const capacity = driver.capacity;
  
  if (!capacity) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Box className="h-4 w-4" />
            السعة والحمولة
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">لا توجد بيانات سعة متاحة</p>
        </CardContent>
      </Card>
    );
  }

  const usedCapacity = parseFloat(String(driver.used_capacity_m3 || 0));
  const usedWeight = parseFloat(String(driver.used_weight_kg || 0));
  
  const shipmentsPercentage = (driver.current_shipments / capacity.max_shipments) * 100;
  const volumePercentage = (usedCapacity / capacity.max_capacity_m3) * 100;
  const weightPercentage = (usedWeight / capacity.max_weight_kg) * 100;

  const stats = [
    {
      label: 'الشحنات الحالية',
      icon: Package,
      current: driver.current_shipments,
      max: capacity.max_shipments,
      available: driver.available_slots ?? 0,
      percentage: shipmentsPercentage,
      unit: 'شحنة',
      availableUnit: 'متاح',
    },
    {
      label: 'الحجم المستخدم',
      icon: Box,
      current: usedCapacity.toFixed(1),
      max: capacity.max_capacity_m3,
      available: driver.available_capacity_m3 ?? 0,
      percentage: volumePercentage,
      unit: 'م³',
      availableUnit: 'م³ متاح',
    },
    {
      label: 'الوزن المستخدم',
      icon: Scale,
      current: usedWeight.toFixed(0),
      max: capacity.max_weight_kg,
      available: driver.available_weight_kg ?? 0,
      percentage: weightPercentage,
      unit: 'كجم',
      availableUnit: 'كجم متاح',
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Box className="h-4 w-4" />
          السعة والحمولة
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isWarning = stat.available === 0 || stat.percentage >= 85;
          
          return (
            <div key={stat.label} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{stat.label}</span>
                  {isWarning && (
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {stat.current} / {stat.max} {stat.unit}
                </span>
              </div>
              
              <div className="relative">
                <div className={cn("h-2 rounded-full", getProgressBgColor(stat.percentage))}>
                  <div
                    className={cn("h-full rounded-full transition-all", getProgressColor(stat.percentage))}
                    style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{stat.percentage.toFixed(0)}% مستخدم</span>
                <span className={cn(
                  "font-medium",
                  stat.available === 0 ? "text-destructive" : "text-green-600"
                )}>
                  {stat.available} {stat.availableUnit}
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
